import sign from "jwt-encode";
import { jwtDecode } from "jwt-decode";
import CryptoJS from "crypto-js";

const SECRET = process.env.REACT_APP_JWT_SECRET;
const PAYLOAD_SECRET = process.env.REACT_APP_PAYLOAD_SECRET;

/**
 * Devuelve true si existe un secret no vacío.
 */
const hasSecret = (s) => typeof s === "string" && s.trim().length > 0;

/**
 * @name encodePayload
 * @description Encrypts the data passed by parameters.
 * @param {object} data
 * @returns {string|null} Encrypted data.
 * @author Jesús Sánchez Fernández
 */
const encodePayload = (data) => {
  try {
    if (!hasSecret(PAYLOAD_SECRET)) {
      console.error(
        "🚀 ~ encodePayload ~ Missing REACT_APP_PAYLOAD_SECRET. Cannot encrypt payload."
      );
      return null;
    }

    // Aseguramos JSON serializable
    const payloadString = JSON.stringify(data ?? {});
    const encryptedPayload = CryptoJS.AES.encrypt(
      payloadString,
      PAYLOAD_SECRET
    ).toString();
    return encryptedPayload;
  } catch (error) {
    console.error("🚀 ~ encodePayload ~ error", error);
    return null;
  }
};

/**
 * @name decodePayload
 * @description Decrypts the data passed by parameters.
 * @param {string} data
 * @returns {object|false} Decrypted data or false if invalid.
 * @author Jesús Sánchez Fernández
 */
const decodePayload = (data) => {
  try {
    if (!hasSecret(PAYLOAD_SECRET)) {
      console.error(
        "🚀 ~ decodePayload ~ Missing REACT_APP_PAYLOAD_SECRET. Cannot decrypt payload."
      );
      return false;
    }

    if (typeof data !== "string" || data.length === 0) return false;

    const decryptedBytes = CryptoJS.AES.decrypt(data, PAYLOAD_SECRET);
    const decryptedPayloadString = decryptedBytes.toString(CryptoJS.enc.Utf8);

    if (!decryptedPayloadString) return false;

    return JSON.parse(decryptedPayloadString);
  } catch (error) {
    console.error("🚀 ~ decodePayload ~ error", error);
    return false;
  }
};

/**
 * @name encodeToken
 * @description Encrypts data passed by parameters.
 * @param {object} data
 * @returns {string|false} Token or false if could not be generated.
 */
export const encodeToken = (data) => {
  try {
    if (!hasSecret(SECRET)) {
      console.error(
        "🚀 ~ encodeToken ~ Missing REACT_APP_JWT_SECRET. Cannot sign token."
      );
      return false;
    }

    const encryptedData = encodePayload(data);
    if (!encryptedData) return false;

    const encoded = sign(
      {
        data: encryptedData,
      },
      SECRET,
      {
        // Se deja tal cual para mantener compatibilidad con el comportamiento actual del proyecto
        expiresIn: "1h",
      }
    );

    return encoded;
  } catch (error) {
    console.error("🚀 ~ encodeToken ~ error", error);
    return false;
  }
};

/**
 * @name decodeToken
 * @description Decrypts the token that is passed to it by parameters.
 * @param {string} token
 * @returns {object|false} Data or false.
 */
export const decodeToken = (token) => {
  try {
    if (typeof token !== "string" || token.length === 0) return false;

    // jwt-decode NO valida firma; solo decodifica el payload.
    // Pasar SECRET como segundo argumento no aplica (y puede romper si no es un options object).
    const decoded = jwtDecode(token);

    const encryptedData = decoded?.data;
    if (typeof encryptedData !== "string" || encryptedData.length === 0)
      return false;

    return decodePayload(encryptedData);
  } catch (error) {
    // Mantiene el comportamiento: devolver false ante cualquier fallo
    return false;
  }
};
